import React, { useState, Fragment, useEffect } from "react";
import styles from "./CciApps.module.css";
import { Card, Col, Input, Row, Spin, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { fetchQryData } from "../../../../api";
import { crawl } from "../../../../api/util";
import { useQuery } from "@tanstack/react-query";
import { notRelCci } from "../../../../api/query-key";
import {
  newApps,
  chngCclApps,
  domainChanges,
  catChngApps,
  nameChngApps,
  breachedApps,
  cardCss,
  tabPageCss,
} from "./columns";
const { Text } = Typography;

const CciApps = (props) => {
  const [renderer, updateRender] = useState("tab1");

  const releaseVersion = props.rel ? String(props.rel) : null;

  const { data: rawData, isLoading } = useQuery({
    queryKey: notRelCci.notify_rel_cci_ver(releaseVersion),
    queryFn: ({ signal }) =>
      fetchQryData(
        { type: "fetch", qry: "fetRelCciChanges", firstVal: releaseVersion },
        signal
      ),
    staleTime: Infinity,
    enabled: !!releaseVersion && !props.loader,
  });

  const data = React.useMemo(() => {
    if (!rawData) return {};
    return {
      newApps: rawData
        .filter((x) => x.request_type === "new_apps")
        .map((x, i) => ({
          id: i,
          new_app_name: x.new_app_name,
          current_category: x.current_category,
          current_ccl: x.current_ccl,
        })),
      domainChanges: [
        ...rawData
          .filter((x) => x.request_type === "new_domains")
          .map((x, i) => ({
            id: `nd_${i}`,
            change_type: "New Domain",
            new_app_name: x.new_app_name,
            old_category: "NA",
            current_category: x.current_category,
            new_domain: x.new_domain,
          })),
        ...rawData
          .filter((x) => x.request_type === "category_changed_domains")
          .map((x, i) => ({
            id: `cd_${i}`,
            change_type: "Remapped Domain",
            new_app_name: x.new_app_name,
            old_category: x.old_category,
            current_category: x.current_category,
            new_domain: x.new_domain,
          })),
      ],
      chngCclApps: rawData
        .filter((x) => x.request_type === "ccl_changed_apps")
        .map((x, i) => ({
          id: i,
          new_app_name: x.new_app_name,
          current_category: x.current_category,
          old_ccl: x.old_ccl,
          current_ccl: x.current_ccl,
        })),
      catChngApps: rawData
        .filter((x) => x.request_type === "category_changed_apps")
        .map((x, i) => ({
          id: i,
          new_app_name: x.new_app_name,
          old_category: x.old_category,
          current_category: x.current_category,
        })),
      appNameChng: rawData
        .filter((x) => x.request_type === "name_changed_apps")
        .map((x, i) => ({
          id: i,
          old_app_name: x.old_app_name,
          new_app_name: x.new_app_name,
        })),
      breachedApps: rawData
        .filter((x) => x.request_type === "breached_apps")
        .map((x, i) => ({
          id: i,
          new_app_name: x.new_app_name,
          current_category: x.current_category,
          breached_date: x.breached_date,
        })),
    };
  }, [rawData]);

  const load = isLoading || props.loader;

  const onTabChng = (key) => {
    updateRender(key);
    props.setSubmit(!props.submit);
  };

  const contentList = {
    tab1: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={newApps}
        setLiveMenu={props.setLiveMenu}
        data={data["newApps"] || []}
        key={"1"}
      />
    ),
    tab2: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={domainChanges}
        setLiveMenu={props.setLiveMenu}
        data={data["domainChanges"] || []}
        key={"2"}
      />
    ),
    tab3: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={chngCclApps}
        setLiveMenu={props.setLiveMenu}
        data={data["chngCclApps"] || []}
        key={"3"}
      />
    ),
    tab4: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={catChngApps}
        setLiveMenu={props.setLiveMenu}
        data={data["catChngApps"] || []}
        key={"4"}
      />
    ),
    tab5: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={nameChngApps}
        setLiveMenu={props.setLiveMenu}
        data={data["appNameChng"] || []}
        key={"5"}
      />
    ),
    tab6: (
      <FetchTabSearch
        submit={props.submit}
        usrRel={props.usrRel}
        col={breachedApps}
        setLiveMenu={props.setLiveMenu}
        data={data["breachedApps"] || []}
        key={"6"}
      />
    ),
  };

  return (
    <div>
      <Row style={{ margin: "5px", padding: "1px" }}>
        <Col className="gutter-row" span={23}>
          <Card
            style={{ width: "100%", minHeight: "80vh" }}
            bodyStyle={cardCss}
            tabList={tabList}
            onTabChange={onTabChng}
          >
            {!load ? (
              contentList[renderer]
            ) : (
              <div className={styles.spinner}>
                <Spin size="small" />
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const FetchTabSearch = (props) => {
  const [searchVal, setSearchVal] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [origData, setOrigData] = useState([]);
  const [searchIndex, setSearchIndex] = useState([]);

  useEffect(() => {
    props.setLiveMenu({ release: false, topMenu: "R", botMenu: "1" });
    const rows = props.data || [];
    setOrigData(rows);
    setFilteredData(rows);
    setSearchIndex(
      rows.map((user) => ({ allValues: crawl(user).toString() }))
    );
  }, [props.data]);

  useEffect(() => {
    if (searchVal) {
      const reqData = searchIndex.map((user, index) => {
        if (user.allValues.toLowerCase().indexOf(searchVal.toLowerCase()) >= 0)
          return origData[index];
        return null;
      });
      setFilteredData(reqData.filter(Boolean));
    } else setFilteredData(origData);
  }, [searchVal, origData, searchIndex]);

  const releaseLabel = props.usrRel
    ? "Release - " + props.usrRel.split("snapshot_v")[1]?.split("_")[0]
    : "";

  return (
    <>
      <div>
        <Row>
          <Col span={24}>
            <Card
              title={
                <Row>
                  <Col>
                    <Text>{releaseLabel}</Text>
                  </Col>
                </Row>
              }
              size="small"
              headStyle={{ backgroundColor: "#F0F8FF", border: 0 }}
              extra={
                <Row>
                  <Col span={21}>
                    <Input
                      onChange={(e) => setSearchVal(e.target.value)}
                      prefix={
                        <SearchOutlined className="site-form-item-icon" />
                      }
                      placeholder="Crawl Search"
                      allowClear
                    />
                  </Col>
                  <Col span={1}></Col>
                </Row>
              }
            >
              <Fragment>
                <Table
                  rowKey="id"
                  size="small"
                  bordered
                  scroll={{ x: 550, y: 900 }}
                  style={{ margin: "2px" }}
                  dataSource={filteredData}
                  columns={props.col(filteredData.length)}
                  loading={false}
                  pagination={{
                    pageSizeOptions: ["10", "30", "60", "100", "1000"],
                    defaultPageSize: ["50"],
                    showSizeChanger: true,
                    style: tabPageCss,
                  }}
                />
              </Fragment>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

const tabList = [
  { key: "tab1", tab: "New Apps" },
  { key: "tab2", tab: "Domain Changes" },
  { key: "tab3", tab: "CCL Changed Apps" },
  { key: "tab4", tab: "Category Changed Apps" },
  { key: "tab5", tab: "Name Changed Apps" },
  { key: "tab6", tab: "Breached Apps" },
];

export default CciApps;
